import { defineStore } from 'pinia';
import axios from '@/services/axios';
import ApiClient from '@/services/apiClient';
import i18n from '@/i18n';
import Room from '@/models/room';
import AccessReader from '@/models/accessReader';
import License from '@/models/license';

const t = (key, params) => i18n.global.t(key, params);

// The licenses that allow Haxo Booking rooms, named as the API names them
const HAXO_BOOKING = 'HAXO BOOKING';

function withStatus(message, error) {
    if (error.response) message += ` (${error.response.status})`;
    return message;
}

// A refused save: the label is taken (409), a reader vanished (404) or the
// installation's HAXO BOOKING licenses allow no more room (402)
function saveError(error, message) {
    switch (error.response?.status) {
        case 402:
            return t('api.roomLicenseNeeded');
        case 409:
            return t('api.roomLabelAlreadyUsed');
        case 404:
            return t('api.roomReaderNotFound');
        default:
            return withStatus(message, error);
    }
}

// Haxo Booking rooms of the installation being edited, the readers they can
// be made of, and the rooms its HAXO BOOKING licenses allow.
export const useRoomStore = defineStore('room', {
    state: () => ({
        facilityId: null,
        rooms: [],
        readers: [],
    }),

    getters: {
        // The installation's readers grouped by hub / LINK, as the API orders them
        readersByHub: (state) => {
            const hubs = [];
            for (const reader of state.readers) {
                let hub = hubs.find(h => h.hub_id === reader.hub_id);
                if (!hub) {
                    hub = { hub_id: reader.hub_id, hub_label: reader.hub_label, readers: [] };
                    hubs.push(hub);
                }
                hub.readers.push(reader);
            }
            return hubs;
        },
    },

    actions: {
        async load(facilityId) {
            this.facilityId = facilityId;
            await Promise.all([this.loadReaders(facilityId), this.loadRooms(facilityId)]);
        },

        async loadRooms(facilityId) {
            try {
                const response = await axios.get(`/conf/facilities/${facilityId}/rooms`);
                this.rooms = ApiClient.convertToType(response.data, [Room]) || [];
                return this.rooms;
            } catch (error) {
                console.error("loadRooms failed", error);
                throw withStatus(t('api.loadRoomsFailed'), error);
            }
        },

        async loadReaders(facilityId) {
            try {
                const response = await axios.get(`/conf/facilities/${facilityId}/readers`);
                this.readers = ApiClient.convertToType(response.data, [AccessReader]) || [];
                return this.readers;
            } catch (error) {
                console.error("loadReaders failed", error);
                throw withStatus(t('api.loadReadersFailed'), error);
            }
        },

        // How many rooms the installation's HAXO BOOKING licenses allow right
        // now, counted as the backend does when a room is declared: the places
        // of the licenses activated on the installation itself (a LINK's do not
        // count) and running at this moment, added up; none without a license.
        // null when the installation cannot be read: the save then tells (402).
        async loadRoomPlaces(facilityId) {
            try {
                const response = await axios.get(`/conf/facilities/${facilityId}`);
                const licenses = ApiClient.convertToType(response.data.licenses, [License]) || [];
                const now = Date.now();
                const running = (license) => license.name === HAXO_BOOKING && license.start_date instanceof Date
                    && license.start_date.getTime() <= now && now < license.start_date.getTime() + license.duration * 86400000;
                return licenses.filter(running).reduce((places, license) => places + license.maximum, 0);
            } catch (error) {
                console.error("loadRoomPlaces failed", error);
                return null;
            }
        },

        // info: { label, reader_ids }
        async createRoom(facilityId, info) {
            try {
                const response = await axios.post(`/conf/facilities/${facilityId}/rooms`, info);
                const room = ApiClient.convertToType(response.data, Room);
                this.rooms.push(room);
                return room;
            } catch (error) {
                console.error("createRoom failed", error);
                throw saveError(error, t('api.createRoomFailed'));
            }
        },

        async updateRoom(facilityId, roomId, info) {
            try {
                const response = await axios.put(`/conf/facilities/${facilityId}/rooms/${roomId}`, info);
                const room = ApiClient.convertToType(response.data, Room);
                const index = this.rooms.findIndex(r => r.id === roomId);
                if (index === -1) {
                    this.rooms.push(room);
                } else {
                    this.rooms.splice(index, 1, room);
                }
                return room;
            } catch (error) {
                console.error("updateRoom failed", error);
                throw saveError(error, t('api.updateRoomFailed'));
            }
        },

        async deleteRoom(facilityId, roomId) {
            try {
                await axios.delete(`/conf/facilities/${facilityId}/rooms/${roomId}`);
                this.rooms = this.rooms.filter(r => r.id !== roomId);
            } catch (error) {
                console.error("deleteRoom failed", error);
                throw withStatus(t('api.deleteRoomFailed'), error);
            }
        },
    },
});
